import { BlockDefinition, isInStudio, LinkProps } from "@softr-io/blocks-sdk";
import { useAppConstants } from "./AppConstantsContext";
import {
    ComponentProps,
    MouseEventHandler,
    MutableRefObject,
    RefObject,
    cloneElement,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useRef,
    useState,
    useSyncExternalStore,
} from "react";
import { fetchPageDefinition, isSamePageFetchTarget, PageDefinition, PageMetadata } from "./fetchPageDefinition";
import { fetchManifest } from "./manifest";
import { fetchManifestV2Block } from "./fetchManifestV2Block";
import { isEqual, keyBy, omit } from "lodash-es";
import { assert, isInModal, isInPreviewIframe, throwAtCaller, timeout } from "@softr/utils";
import { isPreCompiledBlock } from "./PreCompiledBlockDefinition";
import { isCustomCodeBlock } from "./BlockDefinitionUtils";
import { executeScripts } from "./executeScripts";
import { log } from "./logger";
import PQueue from "p-queue";
import { MODAL_CONSTANTS } from "@softr-io/blocks-sdk/react";
import { isNavigationBlocked } from "./useNavigationBlocker";
import { BlockEvents } from "@softr-io/blocks-sdk/legacy-definitions";
import * as v from "valibot";
import { findFirstMappedDetailsBlock } from "./findFirstMappedDetailsBlock";

const initialPageHref = window.location.href;
const PREVIEW_SEARCH_PARAMS = new Set(["autouser", "show-toolbar", "device"]);
// Same delay as useReloadOnDetailsUpdate: the write is not always visible to the
// datasource read path straight away, and reading too early makes the platform cache
// the pre-write record for 10 seconds.
const REFRESH_AFTER_EDIT_DELAY = 2500;

interface SPANavigationProps {
    initialPageDefinitions: readonly BlockDefinition[];
    initialPageId: string;
    appHeaderCustomCode: string;
    initialPageHeaderCustomCode: string;
    initialPageFooterCustomCode: string;
}
interface SPANavigation {
    definitions: readonly BlockDefinition[];
    pageId: string;
    spaIncompatibleBlockHRIDs: string;
}
export function useSPANavigation({
    initialPageDefinitions,
    initialPageId,
    appHeaderCustomCode,
    initialPageHeaderCustomCode,
    initialPageFooterCustomCode,
}: SPANavigationProps): SPANavigation {
    const { spaMode } = useAppConstants();
    const isNavigatingRef = useRef(false);

    const [pageDef, setPage] = useState<PageDefinition>({
        pageId: initialPageId,
        blocks: initialPageDefinitions,
        headerCustomCode: initialPageHeaderCustomCode,
        footerCustomCode: initialPageFooterCustomCode,
        // See note about the metadata initialization in the useEffect below
        metadata: EMPTY_METADATA,
    });
    const { blocks: definitions, pageId } = pageDef;
    const spaIncompatibleBlockHRIDs = definitions
        .filter(isSPAModeIncompatibleBlock)
        .map((b) => b.hrid)
        .join(", ");
    const definitionsRef = useRef(definitions);

    useEffect(() => {
        definitionsRef.current = definitions;
    });

    // By the time we receive "popstate", window.location.href has already been updated to the new URL, which is why
    // we need to store it in a ref.
    // `useScrollRestoration` needs to save the scroll position of the previous page when navigation starts.
    const pageHref = useRef(initialPageHref);
    const { saveScrollPosition, restoreScrollPosition } = useScrollRestoration();

    useEffect(() => {
        if (!spaMode) return;
        fetchPageDefinition.initializeCache(initialPageHref, {
            pageId: initialPageId,
            blocks: initialPageDefinitions,
            headerCustomCode: initialPageHeaderCustomCode,
            footerCustomCode: initialPageFooterCustomCode,
            metadata: EMPTY_METADATA,
        });
        (async function () {
            // softr-page-renderer.js is imported before the initial page's title element
            // is added. This means that we need to start with an empty metadata and
            // then update it once we have the actual metadata.
            const metadata = await getInitialPageMetadata();
            fetchPageDefinition.initializeCache(initialPageHref, {
                pageId: initialPageId,
                blocks: initialPageDefinitions,
                headerCustomCode: initialPageHeaderCustomCode,
                footerCustomCode: initialPageFooterCustomCode,
                metadata,
            });
        })();
    }, [spaMode, initialPageDefinitions, initialPageId, initialPageHeaderCustomCode, initialPageFooterCustomCode]);

    const initialPageCustomCodeExecutedRef = useRef(false);
    const cleanupPageRef = useRef<() => void>(() => {});
    useEffect(() => {
        if (initialPageCustomCodeExecutedRef.current) return;

        initialPageCustomCodeExecutedRef.current = true;
        cleanupPageRef.current = initializeWindowSoftrPageAndInjectCustomCode({
            headerCustomCode: initialPageHeaderCustomCode,
            footerCustomCode: initialPageFooterCustomCode,
        });
    }, [initialPageHeaderCustomCode, initialPageFooterCustomCode]);

    useEffect(() => {
        if (!spaMode) return;
        /**
         * Reads the current page URL from the window location i.e. this must be called after
         * the URL has been updated.
         */
        const updatePage = async (navType: "PUSH" | "BACK" | "REPLACE") => {
            if (isNavigatingRef.current) return;

            const oldHref = pageHref.current;
            const url = window.location.href;

            // This flag is needed because we can have "Scroll to" links in apps,
            // which point to a section of the current page.
            // On clicking such links, we must not re-run the page custom code or re-calculate page blocks
            const oldHrefURL = new URL(oldHref);
            const hasPageChanged =
                oldHrefURL.pathname !== window.location.pathname || oldHrefURL.search !== window.location.search;
            if (!hasPageChanged) {
                pageHref.current = window.location.href;
                return;
            }
            try {
                // Putting this in try/catch because cleanup callbacks can run user
                // supplied custom code. If it throws, we must not block the navigation
                // even if it means continuing with a potentially broken next page.
                cleanupPageRef.current();
            } catch (e) {
                console.error("Error happened when trying to run the page cleanup callbacks", e);
            }

            if (definitionsRef.current.some(isSPAModeIncompatibleBlock)) {
                window.location.reload();
                return;
            }
            pageHref.current = window.location.href;

            isNavigatingRef.current = true;

            const interval = setInterval(() => {
                setNavigationProgress((prev) => {
                    const remaining = 100 - prev;
                    return Math.min(prev + remaining / 20, 85);
                });
            }, 100);
            saveScrollPosition(oldHref);
            try {
                setNavigationProgress(10);
                const { blocks, pageId, headerCustomCode, footerCustomCode, metadata } = await fetchPageDefinition(url);
                if (blocks.some(isSPAModeIncompatibleBlock)) {
                    window.location.reload();
                }

                // If the details page record has a SEO:Slug field, we need to cannonicalize the URL
                // See: https://docs.softr.io/publishing/item-details-seo
                if (metadata.slug) {
                    history.replaceState({}, "", slugifyURL(new URL(window.location.href), metadata.slug));
                    pageHref.current = window.location.href;
                    // TODO: Handle this in preview. I tried to do window.parent.history.replaceState
                    // here, which partially works, but it breaks the back button.
                }
                applyPageMetadata(metadata, hasCustomCanonical(appHeaderCustomCode));
                window.records = {};
                document.getElementById("page-content")!.setAttribute("data-pageid", pageId);

                startViewTransition(() => {
                    setPage((prev) => {
                        const oldDefs = keyBy(prev.blocks, "id");
                        // Since the nav block probably already exists on the last page,
                        // we can re-use the same block definition for the next page.
                        // This ensures that `memo(Block)` is re-used
                        return {
                            blocks: blocks.map((it) => (it.id in oldDefs ? oldDefs[it.id] : it)),
                            pageId: pageId,
                            headerCustomCode,
                            footerCustomCode,
                            metadata: metadata,
                        };
                    });
                });

                restoreScrollPosition(navType);
                setNavigationProgress(100);
                cleanupPageRef.current = initializeWindowSoftrPageAndInjectCustomCode({
                    headerCustomCode,
                    footerCustomCode,
                });
            } finally {
                isNavigatingRef.current = false;
                setNavigationProgress(0);
                clearInterval(interval);
            }
        };
        const popStateListener = async () => {
            // When pressing the back button which only changes the modal param,
            // There's no reason to call updatePage, because the parent page hasn't really changed.
            //
            // Without this check, the page crashes in the following scenario (only in chrome):
            // 1. Open a modal by clicking an "Open in modal" link
            // 2. Within the modal, click on another "Open in modal" link. This should cause the modal's page to change.
            // 3. Click the browser back button (this would fire "popstate" in the parent window, and OpenInModal dialog
            //    would update the iframe)
            // The page would crash (not sure why exactly. Calling `updatePage` on the parent and the modal iframe
            // at the same time when one of the navigations is a "popstate" seems to trigger this crash.)
            // The issue goes away when we update the modal src with a timeout.
            // Or, we can simply avoid calling `updatePage` when only the modal query param has changed.

            if (!onlyModalChanged(pageHref.current, window.location.href)) {
                if (
                    shouldBlockNavigation({
                        currentHref: pageHref.current,
                        targetHref: window.location.href,
                    })
                ) {
                    // If the user cancels the navigation, we need to revert the URL change
                    // that already happened
                    //
                    // Note that this is not perfect because this tramples navigation history when
                    // the user presses "forward" instead of "back"
                    // In that case, we should call history.forward()
                    // instead of pushState, but popstate event doesn't give us a straightforward way
                    // to distinguish between back and forward, so this will have to do for now.
                    // Using the browser forward button is kind of rare anyway so we accept that the
                    // history will get messed up in that case.
                    history.pushState({}, "", pageHref.current);
                    return;
                }
                updatePage("BACK");
            } else {
                // updatePage updates the pageHref, so we need to manually do it when we skip updatePage
                pageHref.current = window.location.href;
            }
            updatePreviewWindowURL();
        };
        const actionClickListener = async (event: SPANavigationEvent) => {
            const url = event.detail.link;
            if (
                shouldBlockNavigation({
                    currentHref: window.location.href,
                    targetHref: url,
                })
            ) {
                return;
            }
            if (event.detail.mode === "PUSH") {
                history.pushState({}, "", url);
            } else {
                history.replaceState({}, "", url);
            }
            updatePage(event.detail.mode);
            updatePreviewWindowURL();
        };

        window.addEventListener("page-renderer:spa-navigation", actionClickListener);
        window.addEventListener("popstate", popStateListener);

        return () => {
            window.removeEventListener("page-renderer:spa-navigation", actionClickListener);
            window.removeEventListener("popstate", popStateListener);
        };
    }, [spaMode, appHeaderCustomCode, saveScrollPosition, restoreScrollPosition]);

    useRefreshBlocksAfterRecordUpdate(
        (pageDefinition) => {
            setPage((prev) => {
                // Most edits don't flip a condition. Bail out so the page doesn't re-render for nothing.
                if (isSameBlockList(pageDefinition.blocks, prev.blocks)) return prev;
                const oldDefs = keyBy(prev.blocks, "id");
                // Reuse definitions of blocks still on the page so memo(Block) doesn't remount them.
                return { ...prev, blocks: pageDefinition.blocks.map((it) => oldDefs[it.id] ?? it) };
            });
        },
        definitionsRef,
        isNavigatingRef,
    );

    if (spaMode) {
        return { definitions, pageId, spaIncompatibleBlockHRIDs };
    } else {
        return {
            definitions: initialPageDefinitions,
            pageId: initialPageId,
            spaIncompatibleBlockHRIDs,
        };
    }
}
function updatePreviewWindowURL() {
    if (!isInPreviewIframe()) return;
    if (isInModal()) return;
    let urlObj: URL;
    try {
        urlObj = new URL(window.location.href);
    } catch (e) {
        console.error("Failed to parse URL", e);
        return;
    }
    // Copy the parent window's search params like 'autoUser' and 'show-toolbar'.
    // This prevents the preview toolbar from disappearing.
    const parentUrlObj = new URL(window.parent.location.href);
    for (const [key, value] of parentUrlObj.searchParams) {
        if (PREVIEW_SEARCH_PARAMS.has(key.toLowerCase())) {
            urlObj.searchParams.set(key, value);
        }
    }

    // In the SPA mode, the history stack is maintained by the app's iframe, not the preview window.
    // That's why this line calls `replaceState` on the preview window, not `pushState`
    window.parent.history.replaceState({}, "", urlObj.href);
}

function normalizeURLForScrollRestoration(href: string) {
    let url: URL;
    try {
        url = new URL(href, window.location.origin);
    } catch (e) {
        console.error("Failed to parse URL in normalizeScrollRestorationURL", e);
        return href;
    }

    url.searchParams.delete("t");
    url.searchParams.delete("device");
    url.searchParams.delete("autoUser");
    url.searchParams.delete("show-toolbar");
    return url.pathname + url.search + url.hash;
}
function useScrollRestoration() {
    const { spaMode } = useAppConstants();
    const savedScrollPositions = useRef<{ [pathname: string]: number }>({});
    const saveScrollPosition = useCallback(
        (previousPageHref: string) => {
            if (!spaMode) return;
            savedScrollPositions.current[normalizeURLForScrollRestoration(previousPageHref)] = window.scrollY;
        },
        [spaMode],
    );
    const restoreScrollPosition = useCallback(
        async (navType: "PUSH" | "BACK" | "REPLACE") => {
            if (!spaMode) return;
            if (navType === "PUSH" || navType === "REPLACE") {
                const hash = window.location.hash.replace(/^#/, "");
                if (hash) {
                    setTimeout(
                        () => {
                            const elem = document.getElementById(hash);
                            if (elem) elem.scrollIntoView({ behavior: "instant" });
                            else window.scrollTo({ top: 0, behavior: "instant" });
                        },
                        // Just a little grace period to let react do its thing before we scroll to the element
                        // We've likely *just* called setState at this point, and the hash element has probably not
                        // rendered.
                        10,
                    );
                } else {
                    window.scrollTo({
                        top: 0,
                        behavior: "instant",
                    });
                }
            } else {
                // This is a bit hacky, but the event is dispatched immediately after the setState call.
                // However, we need to wait for the DOM to actually update before restoring the scroll position.
                // Otherwise, we might end up trying to scroll to an offset which hasn't been rendered yet.
                await new Promise((r) => requestIdleCallback(r));
                const scrollY =
                    savedScrollPositions.current[normalizeURLForScrollRestoration(window.location.href)] ?? 0;
                window.scrollTo({
                    top: scrollY,
                    behavior: "instant",
                });
            }
        },
        [spaMode],
    );

    return { saveScrollPosition, restoreScrollPosition };
}

/**
 * Like SPALink but for cases where a link component cannot be used,
 * such as listening on events bubbling from a container.
 */
export function useSPALinkClick() {
    const { spaMode: spaModeEnabled } = useAppConstants();
    return useCallback(
        (event: MouseEvent) => {
            if (event.defaultPrevented) return;
            // Note that it should always be event.target, not event.currentTarget,
            // because we want the actual clicked element.
            const element = event.target;
            if (!(element instanceof HTMLAnchorElement)) return;

            const href = element.href;
            const target = element.target;
            handleSPALinkClick(event, {
                href,
                target,
                spaModeEnabled,
            });
        },
        [spaModeEnabled],
    );
}
function handleSPALinkClick(
    event: MouseEvent,
    {
        href,
        target,
        spaModeEnabled,
    }: {
        href: string;
        target: string;
        spaModeEnabled: boolean;
    },
) {
    if (process.env.NODE_ENV === "development") {
        // This link click handler is attached on the root element of the block by the vibe block
        // This makes it a bit hard to trace, which is why we're logging a message here in dev mode.
        console.info("Page renderer's SPA link click handler called", { href, target });
    }
    // Copied from wouter's source code which is public domain:
    // https://github.com/molefrog/wouter/blob/b32804fa35aedff1e9bd3da98ae82c5af5c71a61/packages/wouter/src/index.js#L272
    // ignores the navigation when clicked using right mouse button or
    // by holding a special modifier key: ctrl, command, win, alt, shift
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.button !== 0) return;

    if (!spaModeEnabled) return;
    if (!isInternalLink(href)) return;
    if (target === "_blank") return;

    // target="_parent" doesn't necessarily mean target="_parent"
    // This is because when in preview mode, the platform patches all the links on the page
    // to have show-toolbar=true and target="_parent". In the non-spa mode, this was done so that
    // the links would change the preview iframe's URL.
    // However, in the SPA mode, we can manually sync the preview iframe's URL to keep the blocks side simple.
    // The blocks side can stop caring about the preview iframe and the page renderer can sync up the preview
    // window's URL on navigation.
    const actualTarget =
        IN_PREVIEW && !isInModal() && target === "_parent" && href?.includes("show-toolbar=true") ? "_self" : target;

    if (actualTarget === "_parent") return;
    if (isSamePageHashChange(href)) return;
    event.preventDefault();
    window.dispatchEvent(new SPANavigationEvent(href, "PUSH"));
}

function isSamePageHashChange(href: string): boolean {
    if (href.startsWith("#")) {
        return true;
    }
    const url = tryParseURL(href);
    if (!url) return false;

    return (
        url.pathname === window.location.pathname &&
        url.host === window.location.host &&
        url.search === window.location.search &&
        url.hash !== "" &&
        url.hash !== window.location.hash
    );
}

function tryParseURL(href: string): URL | null {
    try {
        return new URL(href, window.location.origin);
    } catch {
        return null;
    }
}
export function spaNavigate(href: string, mode: "PUSH" | "REPLACE") {
    window.dispatchEvent(new SPANavigationEvent(href, mode));
}

export const SPALink = forwardRef<HTMLAnchorElement, LinkProps>(function SPRLink(
    {
        href,
        onClick: propsOnClick,
        asChild,
        prefetch = "render",
        onMouseEnter: propsOnMouseEnter,
        onMouseLeave: propsOnMouseLeave,
        target,
        ...props
    },
    ref,
) {
    const { spaMode: spaModeEnabled, baseUrl } = useAppConstants();
    const spaMode = spaModeEnabled && isInternalLink(href) && target !== "_blank";
    let onClick: MouseEventHandler<HTMLAnchorElement> | undefined;
    if (spaMode) {
        onClick = (event) => {
            propsOnClick?.(event);
            if (event.isDefaultPrevented()) {
                // If the user has done something like this:
                // <Link onClick={e => e.preventDefault()} ... />
                // We need to handle that by having an early return.
                return;
            }

            const elementTarget = event.currentTarget.target;
            const elementHref = event.currentTarget.href;
            handleSPALinkClick(event.nativeEvent, {
                href: elementHref ?? "/",
                target: elementTarget ?? "",
                spaModeEnabled,
            });
        };
    } else {
        onClick = propsOnClick;
    }

    useEffect(() => {
        if (!spaMode) return;
        if (prefetch !== "render") return;
        prefetchPage({ href, baseUrl, queue: renderPrefetchQueue });
    }, [spaMode, href, baseUrl, prefetch]);
    let onMouseEnter: MouseEventHandler<HTMLAnchorElement> | undefined = propsOnMouseEnter;
    let onMouseLeave: MouseEventHandler<HTMLAnchorElement> | undefined = propsOnMouseLeave;
    const intentToPrefetchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    if (spaMode && prefetch === "intent") {
        onMouseEnter = (event) => {
            propsOnMouseEnter?.(event);
            if (event.defaultPrevented) return;
            intentToPrefetchTimeout.current = setTimeout(() => {
                prefetchPage({
                    href,
                    baseUrl,
                    queue: intentPrefetchQueue,
                });
                intentToPrefetchTimeout.current = null;
            }, 100);
        };
        onMouseLeave = (event) => {
            propsOnMouseLeave?.(event);
            if (event.defaultPrevented) return;
            if (intentToPrefetchTimeout.current !== null) {
                clearTimeout(intentToPrefetchTimeout.current);
                intentToPrefetchTimeout.current = null;
            }
        };
    }
    useEffect(() => {
        if (intentToPrefetchTimeout.current !== null) {
            clearTimeout(intentToPrefetchTimeout.current);
        }
    }, []);

    if (asChild) {
        assert(isValidElement(props.children), "SPALink with `asChild` prop must have a single React element as child");
        const child = props.children as React.ReactElement<ComponentProps<"a">>;
        return cloneElement(child, {
            ref,
            href,
            onClick,
            target,
            onMouseEnter,
            onMouseLeave,
            // @ts-expect-error anchor tags *do* support data-attributes
            "data-prefetch": prefetch,
        });
    }
    return (
        <a
            {...props}
            ref={ref}
            href={href}
            target={target}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            data-prefetch={prefetch}
        />
    );
});

const renderPrefetchQueue = new PQueue({
    concurrency: 2,
    // Don't send more than 10 requests in a 5 second window
    interval: 5000,
    intervalCap: 10,

    timeout: 5000,
});
// use a separate queue for intent prefetches so that hover prefetches
// don't get blocked by render prefetches.
const intentPrefetchQueue = new PQueue({
    concurrency: 1,
});

async function prefetchPage({ href, baseUrl, queue }: { href: string | undefined; baseUrl: string; queue: PQueue }) {
    if (isInStudio()) return;
    if (!href) return;
    if (href.startsWith("/")) {
        href = `${window.location.origin}${href}`;
    }
    if (!isInternalLink(href)) {
        // external link
        return;
    }
    if (href.startsWith("#")) {
        return;
    }

    const def = await queue.add(() => fetchPageDefinition(href, { priority: "low" }));
    if (IS_MOBILE_DEVICE) {
        // avoid prefetching too much on mobile devices to avoid excessive memory usage
        return;
    }
    // The idea here is that we want to prefetch the navigation block AND the first block on the page
    const blocksToPrefetch = def.blocks.slice(0, 2);
    for (const block of blocksToPrefetch) {
        if (isPreCompiledBlock(block)) return;
        queue
            .add(() => fetchManifest(block.name, block.version, baseUrl, "low"))
            .then((manifest) => {
                if (manifest.version === "2") {
                    renderPrefetchQueue.add(() =>
                        fetchManifestV2Block({
                            baseUrl,
                            blockName: block.name,
                            blockVersion: block.version,
                            manifest,
                        }),
                    );
                }
            });
    }
}
const IS_MOBILE_DEVICE = /(Android)|(iPhone)/.test(navigator.userAgent);

class SPANavigationEvent extends CustomEvent<{ link: string; mode: "PUSH" | "REPLACE" }> {
    constructor(link: string, mode: "PUSH" | "REPLACE") {
        super("page-renderer:spa-navigation", { detail: { link, mode } });
    }
}

declare global {
    interface WindowEventMap {
        "page-renderer:spa-navigation": SPANavigationEvent;
    }
}

export function addNavigationListener(listener: () => void): () => void {
    window.addEventListener("page-renderer:spa-navigation", listener);
    window.addEventListener("popstate", listener);
    window.addEventListener("hashchange", listener);
    return () => {
        window.removeEventListener("page-renderer:spa-navigation", listener);
        window.removeEventListener("popstate", listener);
        window.removeEventListener("hashchange", listener);
    };
}

function startViewTransition(cb: () => void) {
    if ("startViewTransition" in document && typeof document.startViewTransition === "function") {
        document.startViewTransition(cb);
    } else {
        cb();
    }
}

const IN_PREVIEW = isInPreviewIframe();

function useRefreshBlocksAfterRecordUpdate(
    apply: (pageDefinition: PageDefinition) => void,
    definitionsRef: MutableRefObject<readonly BlockDefinition[]>,
    isNavigatingRef?: RefObject<boolean>,
) {
    const applyRef = useRef(apply);
    useEffect(() => {
        applyRef.current = apply;
    });

    useEffect(() => {
        if (isInStudio()) return;

        const unmounted = new AbortController();
        let pending: AbortController | null = null;

        const refresh = async (signal: AbortSignal) => {
            const href = window.location.href;
            await timeout(REFRESH_AFTER_EDIT_DELAY, { signal });
            // `timeout` resolves instead of throwing when the signal aborts
            if (signal.aborted || isNavigatingRef?.current) return;
            if (!isSamePageFetchTarget(window.location.href, href)) return;

            try {
                fetchPageDefinition.invalidate(href);
                const pageDefinition = await fetchPageDefinition(href);
                if (signal.aborted) return;
                if (!isSamePageFetchTarget(window.location.href, href)) return;
                applyRef.current(pageDefinition);
            } catch (e) {
                // A rejected promise stays in the cache and breaks later navigation here.
                fetchPageDefinition.invalidate(href);
                log.warn("Failed to refresh the page blocks after a record update", e);
            }
        };

        const scheduleRefresh = () => {
            // Measure the delay from the last edit, not the first one.
            pending?.abort();
            pending = new AbortController();
            void refresh(pending.signal);
        };

        window.addEventListener(BlockEvents.UPDATE_RECORD_SUCCESS, scheduleRefresh, { signal: unmounted.signal });
        // Linked details blocks, lists, forms and workflows only dispatch this one.
        window.addEventListener(
            BlockEvents.UPDATE_DATASOURCE,
            (event) => {
                if (updatesDetailsPageTable(event, definitionsRef.current)) scheduleRefresh();
            },
            { signal: unmounted.signal },
        );

        return () => {
            pending?.abort();
            unmounted.abort();
        };
    }, [definitionsRef, isNavigatingRef]);
}

const UpdateDatasourceDetailSchema = v.object({
    triggeredBy: v.string(),
    dataSource: v.optional(
        v.looseObject({
            integrationId: v.string(),
            resourceIdentifier: v.looseObject({ keys: v.record(v.string(), v.unknown()) }),
        }),
    ),
});

function updatesDetailsPageTable(event: Event, definitions: readonly BlockDefinition[]) {
    if (!(event instanceof CustomEvent)) return false;
    const parsed = v.safeParse(UpdateDatasourceDetailSchema, event.detail);
    if (!parsed.success) return false;
    const { triggeredBy, dataSource } = parsed.output;
    if (triggeredBy === "workflow") return true;

    const detailsDataSource = findFirstMappedDetailsBlock(definitions)?.collection.dataSources.find(
        (ds) => ds.referenceType === "BLOCK",
    );
    if (!dataSource || !detailsDataSource) return false;
    // Same check as useDatasourceUpdate: all Airtable views point to the same table
    return (
        dataSource.integrationId === detailsDataSource.integrationId &&
        isEqual(
            omit(dataSource.resourceIdentifier.keys, "view"),
            omit(detailsDataSource.resourceIdentifier.keys, "view"),
        )
    );
}

function isSameBlockList(a: readonly BlockDefinition[], b: readonly BlockDefinition[]) {
    return a.length === b.length && a.every((block, i) => block.id === b[i].id);
}

function isSPAModeIncompatibleBlock(block: BlockDefinition) {
    return (
        (isCustomCodeBlock(block) && block.version[0] !== "5") || (block.type === "dynamic" && block.version[0] !== "5")
    );
}

const ALLOWED_TAGS_IN_HEADER = new Set([
    // List copied from https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/head#see_also
    "title",
    "base",
    "link",
    "style",
    "meta",
    "script",
    "noscript",
    "template",
]);

function initializeWindowSoftrPageAndInjectCustomCode(
    pageDef: Pick<PageDefinition, "headerCustomCode" | "footerCustomCode">,
): () => void {
    const injectedNodes: Node[] = [];

    if (pageDef.headerCustomCode) {
        const container = document.createElement("div");
        container.innerHTML = pageDef.headerCustomCode;

        const shouldRenderInsideHead =
            // Since the studio editor has no validation, we have to check if the user's supplied header custom
            // code is valid to be placed inside <head>
            // Otherwise, it doesn't work as expected
            Array.from(container.children).every((child) => ALLOWED_TAGS_IN_HEADER.has(child.tagName.toLowerCase()));
        if (!shouldRenderInsideHead) {
            log.warn(
                "Page header custom code contains tags that are not allowed inside <head>. Injecting into <body> instead.",
            );
        }
        const startComment = document.createComment("Page header custom code");
        const parent = shouldRenderInsideHead ? document.head : document.body;

        parent.appendChild(startComment);
        injectedNodes.push(startComment);

        for (const child of Array.from(container.children)) {
            parent.appendChild(child);
            injectedNodes.push(child);
        }

        const endComment = document.createComment("End of page header custom code");
        document.head.appendChild(endComment);
        injectedNodes.push(endComment);
    }
    if (pageDef.footerCustomCode) {
        const startComment = document.createComment("Page footer custom code");
        document.body.appendChild(startComment);
        injectedNodes.push(startComment);

        const container = document.createElement("div");
        container.innerHTML = pageDef.footerCustomCode;
        for (const child of Array.from(container.children)) {
            document.body.appendChild(child);
            injectedNodes.push(child);
        }

        const endComment = document.createComment("End of page footer custom code");
        document.body.appendChild(endComment);
        injectedNodes.push(endComment);
    }

    const cleanupCallbacks: (() => void)[] = [];
    const pageAbortController = new AbortController();

    async function waitFor(condition: () => unknown) {
        if (typeof condition !== "function") {
            throwAtCaller(
                waitFor,
                new Error("waitFor must be called with a function argument. Did you forget a '() => '?"),
            );
        }
        while (!condition()) {
            await timeout(50, { signal: pageAbortController.signal });
        }
    }
    window.SOFTR_PAGE = {
        beforeUnload(cb: () => void) {
            cleanupCallbacks.push(cb);
        },
        // Since SPA mode page code can't rely on DOMContentLoaded event,
        // we provide a simple utility to wait for a condition to be true
        // It's a bit tricky to write by hand because of abort controllers.
        waitFor,
    };

    for (const node of injectedNodes) {
        if (node instanceof HTMLElement) {
            executeScripts(node);
        }
    }

    return async () => {
        pageAbortController.abort();
        cleanupCallbacks.reverse().forEach((cb) => {
            try {
                cb();
            } catch (e) {
                console.error("Error in page custom code during beforeUnload callback:", e);
            }
        });
        for (const node of injectedNodes) {
            node.parentNode?.removeChild(node);
        }
    };
}

function isInternalLink(href: string | undefined): boolean {
    if (!href) return false;
    // Relative URLs are always internal
    if (href.startsWith("/")) return true;
    try {
        const url = new URL(href, window.location.origin);
        return url.origin === window.location.origin;
    } catch {
        // If URL parsing fails, treat as external
        return false;
    }
}

declare global {
    interface Window {
        SOFTR_PAGE: {
            beforeUnload: (cb: () => void) => void;
            waitFor: (condition: () => unknown) => Promise<void>;
        };
    }
}

const EMPTY_METADATA: PageMetadata = {
    title: "",
    description: "",
    slug: null,
    socialTitle: null,
    socialDescription: null,
    socialImage: null,
    canonicalUrl: undefined,
};
async function getInitialPageMetadata(): Promise<PageMetadata> {
    // Bit of a hack, but the document title gets set pretty early relatively,
    // so 50 ms should be enough.
    // TODO: Move the page renderer script after title and other metadata tags to avoid having
    //       to do this.
    await timeout(50);
    return {
        title: window.document.title,
        description: document.querySelector('meta[name="description"]')?.getAttribute("content") ?? "",
        slug: location.href.match(/\/(?<slug>[^/]+)\/r\/[^/]+$/)?.groups?.slug ?? null,
        socialTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content") ?? null,
        socialDescription: document.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? null,
        socialImage: document.querySelector('meta[property="og:image"]')?.getAttribute("content") ?? null,
        canonicalUrl: document.head.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? null,
    };
}
function applyPageMetadata(metadata: PageMetadata, appCustomCodeOwnsCanonical: boolean) {
    document.title = metadata.title;
    if (isInPreviewIframe()) {
        window.parent.document.title = metadata.title;
    }

    applyMetaTag("name", "description", metadata.description);

    // The platform uses <meta name> and <meta property> inconsistently
    // this function tries to match that exact behaviour.
    // Some of these have both <meta name> and <meta property> tags with the same content.
    // This is intentional to keep parity with the platform.
    applyMetaTag("name", "twitter:title", metadata.socialTitle);
    applyMetaTag("property", "twitter:title", metadata.socialTitle);
    applyMetaTag("property", "og:title", metadata.socialTitle);
    applyMetaTag("name", "twitter:description", metadata.socialDescription);
    applyMetaTag("property", "twitter:description", metadata.socialDescription);
    applyMetaTag("property", "og:description", metadata.socialDescription);
    applyMetaTag("property", "og:image", metadata.socialImage);
    applyMetaTag("name", "twitter:image", metadata.socialImage);
    applyMetaTag("name", "twitter:card", metadata.socialImage ? "summary_large_image" : null);
    applyMetaTag("property", "og:url", window.location.href);
    applyMetaTag("property", "twitter:url", window.location.href);
    // App custom code stays on the page across navigation and manages its own canonical
    if (metadata.canonicalUrl !== undefined && !appCustomCodeOwnsCanonical) {
        applyCanonicalLink(metadata.canonicalUrl);
    }
}

/**
 * Same check as the platform's CanonicalUrlResolver.customHeaderContainCanonicalUrl
 */
function hasCustomCanonical(customCode: string): boolean {
    return /rel=["']canonical["']/i.test(customCode);
}

/**
 * Runs after the previous page's custom code is removed and before the next page's is injected,
 * so with no canonical in the app custom code, any canonical link in <head> is Softr's.
 * Only <head> is searched: the previous page's blocks are still mounted in <body> at this point.
 * The next page's custom code may add its own afterwards, same as in the server-rendered page.
 */
function applyCanonicalLink(href: string | null) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (href === null) {
        link?.remove();
        return;
    }
    if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
    }
    link.setAttribute("href", href);
}

/**
 * Inserts, updates or removes a meta tag in the document head.
 *
 * applyMetaTag("name", "og:description", "My page description")
 *   => <meta name="og:description" content="My page description">
 * applyMetaTag("property", "og:description", "My page description")
 *   => <meta property="og:description" content="My page description">
 * When content is null, removes the meta tag if it exists.
 */
function applyMetaTag(key: "name" | "property", name: string, content: string | null) {
    if (content) {
        let metaTag = document.querySelector(`meta[${key}="${name}"]`);
        if (!metaTag) {
            metaTag = document.createElement("meta");
            metaTag.setAttribute(key, name);
            document.head.appendChild(metaTag);
        }
        metaTag.setAttribute("content", content);
    } else {
        const metaTag = document.querySelector(`meta[${key}="${name}"]`);
        if (metaTag) {
            document.head.removeChild(metaTag);
        }
    }
}
function slugifyURL(url: URL, slug: string): string {
    if (!slug) return url.toString();
    const recordIdEntry = [...url.searchParams.entries()].find(([key]) => key.toLowerCase() === "recordid");
    if (!recordIdEntry) return url.toString();
    const recordId = recordIdEntry[1];
    url.searchParams.delete(recordIdEntry[0]);

    if (!url.pathname.endsWith("/")) {
        url.pathname += "/";
    }
    url.pathname += `${encodeURIComponent(slug)}/r/${encodeURIComponent(recordId)}`;
    return url.toString();
}

let spaNavigationProgress = 0;
const getSPANavigationProgressSnapshot = () => spaNavigationProgress;
const subscribeToSPANavigationProgress = (callback: () => void) => {
    window.addEventListener("softr-page-renderer:spa-navigation-progress", callback);
    return () => {
        window.removeEventListener("softr-page-renderer:spa-navigation-progress", callback);
    };
};
export function useSPANavigationProgress(): number {
    return useSyncExternalStore(subscribeToSPANavigationProgress, getSPANavigationProgressSnapshot);
}

function setNavigationProgress(value: number | ((prev: number) => number)) {
    if (typeof value === "function") {
        spaNavigationProgress = value(spaNavigationProgress);
    } else {
        spaNavigationProgress = value;
    }
    const event = new CustomEvent("softr-page-renderer:spa-navigation-progress");
    window.dispatchEvent(event);
}
function onlyModalChanged(oldHref: string, newHref: string): boolean {
    const oldUrl = new URL(oldHref);
    const newUrl = new URL(newHref);

    // Normalize the URL by removing modal related search params
    for (const key of [MODAL_CONSTANTS.PARAM_PLACEMENT, MODAL_CONSTANTS.PARAM_SRC, MODAL_CONSTANTS.PARAM_SIZE]) {
        oldUrl.searchParams.delete(key);
        newUrl.searchParams.delete(key);
    }

    return oldUrl.href === newUrl.href;
}

function shouldBlockNavigation({ currentHref, targetHref }: { currentHref: string; targetHref: string }): boolean {
    const url = tryParseURL(targetHref);
    const previousURL = tryParseURL(currentHref);
    if (!url || !previousURL) return false;
    // Don't block if the page is not changing
    if (
        url.pathname === previousURL.pathname &&
        url.origin === previousURL.origin &&
        url.search === previousURL.search
    ) {
        return false;
    }
    // We treat tab block changes as nav blocking, even if the tab doesn't contain the block which is
    // causing the navigation block, so this can cause some false positives.
    // TODO: Allow navigation when it's a tab change that doesn't involve the blocking block
    return (
        isNavigationBlocked() &&
        !confirm(
            "You have unsaved changes that will be lost if you navigate away from this page. Do you want to continue?",
        )
    );
}
